import { Component } from '@theme/component';

/**
 * Partner logo rail
 *
 * The rail itself is a CSS scroll-snap container and works without this script —
 * swipe, shift+wheel and keyboard all scroll it. This only adds the arrow buttons,
 * which page it one screenful at a time and disable at each end.
 *
 * @typedef {Object} Refs
 * @extends {Component<Refs>}
 */
class B2bPartnerRail extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);

    this.#rail?.addEventListener('scroll', this.#syncArrows, { passive: true });
    window.addEventListener('resize', this.#syncArrows, { passive: true });

    this.#syncArrows();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#rail?.removeEventListener('scroll', this.#syncArrows);
    window.removeEventListener('resize', this.#syncArrows);
  }

  get #rail() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-rail]'));
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const prev = target.closest('[data-rail-prev]');
    const next = target.closest('[data-rail-next]');
    if (!prev && !next) return;

    const rail = this.#rail;
    if (!rail) return;

    // One screenful per press, matching the paged feel of the design.
    rail.scrollBy({ left: prev ? -rail.clientWidth : rail.clientWidth, behavior: 'smooth' });
  };

  #syncArrows = () => {
    const rail = this.#rail;
    if (!rail) return;

    const max = rail.scrollWidth - rail.clientWidth;
    const atStart = rail.scrollLeft <= 1;
    const atEnd = rail.scrollLeft >= max - 1;
    // Nothing to page through: hide both rather than showing two dead controls.
    const fits = max <= 1;

    for (const [selector, disabled] of [
      ['[data-rail-prev]', atStart],
      ['[data-rail-next]', atEnd],
    ]) {
      const button = this.querySelector(String(selector));
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = Boolean(disabled) || fits;
      button.hidden = fits;
    }
  };
}

if (!customElements.get('b2b-partner-rail')) {
  customElements.define('b2b-partner-rail', B2bPartnerRail);
}
